import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Plus, Trash2, Calendar, Users, X, Save, Loader2, FileDown, Sparkles,
  ChefHat, Wine, Search, ChevronRight,
} from "lucide-react";
import api, { formatApiError, API } from "../api";
import {
  Button, Card, Input, Select, Label, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";
import DateInputAT from "../components/DateInputAT";
import { formatEUR as fmtEur } from "../utils/format";

const fmtNum = (v, d = 2) => `${Number(v || 0).toFixed(d).replace(".", ",")}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-AT") : "—";

const EVENT_ICONS = {
  hochzeit: "💍", firmenessen: "🏢", weihnachtsfeier: "🎄", geburtstag: "🎂", sonstiges: "🍽️",
};

export default function EventMenusPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, r, c, m] = await Promise.all([
        api.get("/event-menus"),
        api.get("/recipes"),
        api.get("/cocktails").catch(() => ({ data: { items: [] } })),
        api.get("/menus").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(ev.data.items);
      setRecipes(r.data.items || []);
      setCocktails(c.data.items || []);
      setMenus(m.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm(t("events.confirm_delete"))) return;
    try {
      await api.delete(`/event-menus/${id}`);
      toast.success(t("events.deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <Spinner size={24} />;

  return (
    <div className="space-y-6 fade-in" data-testid="event-menus-page">
      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
            <Sparkles size={12} /> {t("events.kicker")}
          </div>
          <h1 className="text-2xl font-semibold text-ink-primary">{t("events.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1 max-w-2xl">{t("events.subtitle")}</p>
        </div>
        <Button onClick={() => setEditing({ name: "", event_type: "hochzeit", guest_count: 50, courses: [{ course_number: 1, title: "", recipe_ids: [], cocktail_ids: [] }], notes: "" })} data-testid="event-new">
          <Plus size={14} /> {t("events.new")}
        </Button>
      </header>

      {items.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title={t("events.empty_title")}
          description={t("events.empty_sub")}
          action={<Button onClick={() => setEditing({ name: "", event_type: "hochzeit", guest_count: 50, courses: [{ course_number: 1, title: "", recipe_ids: [], cocktail_ids: [] }], notes: "" })}><Plus size={14} /> {t("events.new")}</Button>}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="events-grid">
          {items.map((e) => (
            <Card key={e.id} className="p-4 hover:border-brand/40 transition-colors cursor-pointer" onClick={() => setEditing(e)} data-testid={`event-card-${e.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="text-3xl">{EVENT_ICONS[e.event_type] || "🍽️"}</div>
                <Badge tone="brand">{t(`events.type_${e.event_type}`, e.event_type)}</Badge>
              </div>
              <h3 className="font-semibold text-ink-primary mb-1">{e.name}</h3>
              <div className="flex items-center gap-3 text-xs text-ink-muted">
                <span className="flex items-center gap-1"><Users size={11} /> {e.guest_count}</span>
                <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(e.event_date)}</span>
                <span className="flex items-center gap-1"><ChefHat size={11} /> {(e.courses || []).length}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <EventEditor
          initial={editing}
          recipes={recipes}
          cocktails={cocktails}
          menus={menus}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={(id) => { setEditing(null); del(id); }}
        />
      )}
    </div>
  );
}


function EventEditor({ initial, recipes, cocktails, menus, onClose, onSaved, onDelete }) {
  const { t } = useTranslation();
  // Phase 32 — migrate legacy single-dish courses into multi-dish lists on open
  const migrateCourse = (c) => ({
    course_number: c.course_number,
    title: c.title || "",
    recipe_ids: (c.recipe_ids && c.recipe_ids.length)
      ? c.recipe_ids
      : (c.recipe_id ? [c.recipe_id] : []),
    cocktail_ids: (c.cocktail_ids && c.cocktail_ids.length)
      ? c.cocktail_ids
      : (c.pairing_cocktail_id ? [c.pairing_cocktail_id] : []),
  });
  const [form, setForm] = useState({
    ...initial,
    courses: (initial.courses || []).map(migrateCourse),
  });
  const [calc, setCalc] = useState(initial.calc || null);
  const [saving, setSaving] = useState(false);
  const [showMenuImport, setShowMenuImport] = useState(false);
  const isNew = !initial.id;

  // Live preview
  useEffect(() => {
    let cancelled = false;
    const tm = setTimeout(async () => {
      try {
        const { data } = await api.post("/event-menus/preview", form);
        if (!cancelled) setCalc(data);
      } catch { /* swallow */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(tm); };
  }, [form]);

  const patch = (delta) => setForm((f) => ({ ...f, ...delta }));
  const updateCourse = (idx, delta) => {
    const courses = [...form.courses];
    courses[idx] = { ...courses[idx], ...delta };
    patch({ courses });
  };
  const addCourse = () => {
    if (form.courses.length >= 10) return;
    patch({ courses: [...form.courses, { course_number: form.courses.length + 1, title: "", recipe_ids: [], cocktail_ids: [] }] });
  };
  const removeCourse = (idx) => {
    const c = form.courses.filter((_, i) => i !== idx).map((x, i) => ({ ...x, course_number: i + 1 }));
    patch({ courses: c });
  };

  // Phase 32 — multi-dish helpers
  const addDishToCourse = (idx, kind, dishId) => {
    if (!dishId) return;
    const courses = [...form.courses];
    const key = kind === "recipe" ? "recipe_ids" : "cocktail_ids";
    const list = courses[idx][key] || [];
    if (list.includes(dishId)) return;
    courses[idx] = { ...courses[idx], [key]: [...list, dishId] };
    patch({ courses });
  };
  const removeDishFromCourse = (idx, kind, dishId) => {
    const courses = [...form.courses];
    const key = kind === "recipe" ? "recipe_ids" : "cocktail_ids";
    courses[idx] = { ...courses[idx], [key]: (courses[idx][key] || []).filter((x) => x !== dishId) };
    patch({ courses });
  };

  // Phase 32 — import an existing menu's sections+items as courses
  const importFromMenu = async (menuId) => {
    if (!menuId) return;
    try {
      const { data } = await api.get(`/menus/${menuId}`);
      const newCourses = (data.sections || []).map((sec, i) => {
        const recipe_ids = (sec.items || [])
          .filter((it) => it.kind === "recipe" && it.dish_id)
          .map((it) => it.dish_id);
        const cocktail_ids = (sec.items || [])
          .filter((it) => it.kind === "cocktail" && it.dish_id)
          .map((it) => it.dish_id);
        return {
          course_number: i + 1,
          title: sec.name || sec.type || `Gang ${i + 1}`,
          recipe_ids,
          cocktail_ids,
        };
      });
      if (!newCourses.length) {
        toast.error(t("events.menu_empty", "Das ausgewählte Menü enthält keine Gerichte."));
        return;
      }
      patch({ courses: newCourses.slice(0, 10) });
      setShowMenuImport(false);
      toast.success(t("events.menu_imported", "{{n}} Gänge aus dem Menü übernommen.", { n: newCourses.length }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const recipeById = (id) => recipes.find((r) => r.id === id);
  const cocktailById = (id) => cocktails.find((c) => c.id === id);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error(t("events.name_required"));
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/event-menus", form);
        toast.success(t("events.created"));
      } else {
        await api.put(`/event-menus/${initial.id}`, form);
        toast.success(t("events.updated"));
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const downloadPdf = () => {
    if (!initial.id) return;
    window.open(`${API}/event-menus/${initial.id}/shopping-list.pdf`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-bg-void border-l border-white/10 w-full max-w-5xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="event-editor">
        <header className="sticky top-0 bg-bg-void border-b border-white/10 px-6 py-4 z-10 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-brand">{isNew ? t("events.new") : t("events.edit")}</div>
            <h2 className="text-lg font-semibold">{form.name || t("events.untitled")}</h2>
          </div>
          <div className="flex gap-2">
            {!isNew && <Button variant="ghost" size="sm" onClick={downloadPdf} data-testid="event-pdf"><FileDown size={14} /> {t("events.shopping_pdf")}</Button>}
            {!isNew && <Button variant="ghost" size="sm" onClick={() => onDelete(initial.id)}><Trash2 size={14} className="text-err" /></Button>}
            <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /></Button>
          </div>
        </header>

        <div className="p-6 grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-4">
            <Card className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("events.name")} *</Label>
                  <Input value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Hochzeit Müller" data-testid="event-name" />
                </div>
                <div>
                  <Label>{t("events.event_type")}</Label>
                  <Select value={form.event_type} onChange={(e) => patch({ event_type: e.target.value })} data-testid="event-type">
                    {["hochzeit", "firmenessen", "weihnachtsfeier", "geburtstag", "sonstiges"].map((k) => (
                      <option key={k} value={k}>{t(`events.type_${k}`)}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>{t("events.guest_count")}</Label>
                  <Input type="number" min={1} value={form.guest_count} onChange={(e) => patch({ guest_count: Number(e.target.value) || 1 })} data-testid="event-guests" />
                </div>
                <div>
                  <Label>{t("events.event_date")}</Label>
                  <DateInputAT value={form.event_date || ""} onChange={(iso) => patch({ event_date: iso })} testId="event-date" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="font-semibold text-sm">{t("events.courses")}</div>
                <div className="flex items-center gap-2">
                  {menus && menus.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setShowMenuImport((v) => !v)} data-testid="event-import-menu-btn">
                      <FileDown size={12} /> {t("events.import_menu", "Bestehendes Menü importieren")}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={addCourse} data-testid="event-add-course" disabled={form.courses.length >= 10}>
                    <Plus size={12} /> {t("events.add_course")}
                  </Button>
                </div>
              </div>

              {showMenuImport && (
                <div className="mb-3 p-3 rounded-md bg-bg-void border border-brand/30" data-testid="event-menu-import-panel">
                  <Label className="text-[11px]">{t("events.pick_menu", "Menü auswählen")}</Label>
                  <Select
                    onChange={(e) => importFromMenu(e.target.value)}
                    defaultValue=""
                    data-testid="event-menu-import-select"
                  >
                    <option value="" disabled>—</option>
                    {menus.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({(m.sections || []).length} {t("events.sections", "Sektionen")})
                      </option>
                    ))}
                  </Select>
                  <p className="text-[10px] text-ink-muted mt-1">
                    {t("events.import_hint", "Alle Sektionen werden zu Gängen, alle Gerichte/Drinks übernommen.")}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {form.courses.map((c, i) => (
                  <div key={i} className="bg-bg-void rounded-md p-3 border border-white/5" data-testid={`event-course-${i}`}>
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <Badge>{t("events.course_n", { n: c.course_number })}</Badge>
                      <Input
                        value={c.title || ""}
                        onChange={(e) => updateCourse(i, { title: e.target.value })}
                        placeholder={t("events.course_title_ph", "z. B. Vorspeise")}
                        className="flex-1 text-sm"
                        data-testid={`event-course-title-${i}`}
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeCourse(i)} aria-label="remove course"><Trash2 size={12} className="text-err" /></Button>
                    </div>

                    {/* Dish chips (recipes) */}
                    {(c.recipe_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2" data-testid={`event-course-recipes-${i}`}>
                        {c.recipe_ids.map((rid) => {
                          const r = recipeById(rid);
                          return (
                            <Badge key={rid} tone="brand" className="flex items-center gap-1">
                              <ChefHat size={10} /> {r ? r.name : rid}
                              <button
                                onClick={() => removeDishFromCourse(i, "recipe", rid)}
                                className="ml-1 hover:text-err"
                                data-testid={`event-course-${i}-remove-recipe-${rid}`}
                              ><X size={10} /></button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    {/* Dish chips (cocktails) */}
                    {(c.cocktail_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2" data-testid={`event-course-cocktails-${i}`}>
                        {c.cocktail_ids.map((cid) => {
                          const ck = cocktailById(cid);
                          return (
                            <Badge key={cid} tone="warn" className="flex items-center gap-1">
                              <Wine size={10} /> {ck ? ck.name : cid}
                              <button
                                onClick={() => removeDishFromCourse(i, "cocktail", cid)}
                                className="ml-1 hover:text-err"
                                data-testid={`event-course-${i}-remove-cocktail-${cid}`}
                              ><X size={10} /></button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    {/* Add-dish pickers (multi-add — selecting from dropdown appends) */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px]">{t("events.add_recipe", "Gericht hinzufügen")}</Label>
                        <Select
                          value=""
                          onChange={(e) => { addDishToCourse(i, "recipe", e.target.value); e.target.value = ""; }}
                          data-testid={`event-course-add-recipe-${i}`}
                        >
                          <option value="">{t("events.pick_dish", "— wählen —")}</option>
                          {recipes
                            .filter((r) => !(c.recipe_ids || []).includes(r.id))
                            .map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px]">{t("events.add_cocktail", "Cocktail hinzufügen")}</Label>
                        <Select
                          value=""
                          onChange={(e) => { addDishToCourse(i, "cocktail", e.target.value); e.target.value = ""; }}
                          data-testid={`event-course-add-cocktail-${i}`}
                        >
                          <option value="">{t("events.pick_drink", "— wählen —")}</option>
                          {cocktails
                            .filter((cc) => !(c.cocktail_ids || []).includes(cc.id))
                            .map((cc) => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="event-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {t("events.save")}
              </Button>
            </div>
          </div>

          {/* Live preview */}
          <div className="space-y-3 lg:sticky lg:top-4 self-start">
            <Card className="p-4">
              <div className="text-xs uppercase tracking-wider text-ink-muted mb-3">{t("events.live_preview")}</div>
              {calc ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-ink-muted">{t("events.cost_total")}</span><span className="num font-medium">{fmtEur(calc.total_cost)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-muted">{t("events.cost_per_guest")}</span><span className="num text-brand">{fmtEur(calc.cost_per_guest)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-muted">{t("events.suggested_price")}</span><span className="num text-ok">{fmtEur(calc.suggested_price_per_guest)}</span></div>
                  <div className="border-t border-white/5 pt-2 mt-2">
                    <div className="flex justify-between"><span className="text-ink-muted">{t("events.total_revenue")}</span><span className="num">{fmtEur(calc.total_revenue)}</span></div>
                    <div className="flex justify-between"><span className="text-ink-muted">{t("events.total_db1")}</span><span className="num text-ok">{fmtEur(calc.total_db1)}</span></div>
                  </div>
                </div>
              ) : <Spinner size={16} />}
            </Card>

            {calc?.shopping_list?.length > 0 && (
              <Card className="p-4 max-h-[480px] overflow-y-auto" data-testid="event-shopping-list">
                <div className="text-xs uppercase tracking-wider text-ink-muted mb-2">{t("events.shopping_list")}</div>
                {calc.shopping_list.map((g) => (
                  <div key={g.category} className="mb-3">
                    <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{g.category}</div>
                    {g.items.map((it) => (
                      <div key={it.ingredient_id} className="flex justify-between text-xs py-0.5">
                        <span className="text-ink-secondary truncate">{it.ingredient_name}</span>
                        <span className="num text-ink-primary">{fmtNum(it.total_qty)} {it.unit}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
